/**
 * Asteroids Game
 * A classic arcade game where the player controls a spaceship and destroys asteroids.
 */

import { getHighScores, type HighScoreEntry } from '../state/highscore'

// ============================================================================
// Types
// ============================================================================

/** Callback function called when game ends */
export type GameOverCallback = (score: number) => void

interface Vector {
  x: number
  y: number
}

interface Ship {
  position: Vector
  velocity: Vector
  angle: number
  radius: number
  invulnerable: boolean
  invulnerableTimer: number
}

interface Asteroid {
  position: Vector
  velocity: Vector
  radius: number
  vertices: Vector[]
}

interface Bullet {
  position: Vector
  velocity: Vector
  lifetime: number
}

interface GameState {
  ship: Ship
  asteroids: Asteroid[]
  bullets: Bullet[]
  score: number
  lives: number
  gameOver: boolean
  paused: boolean
  level: number
  started: boolean
}

interface InputState {
  left: boolean
  right: boolean
  thrust: boolean
  reverse: boolean
  shoot: boolean
}

// ============================================================================
// Constants
// ============================================================================

const SHIP_ROTATION_SPEED = 0.1
const SHIP_THRUST = 0.15
const SHIP_MAX_SPEED = 8
const SHIP_FRICTION = 0.99
const SHIP_RADIUS = 15
const SHIP_INVULNERABLE_TIME = 180 // frames (3 seconds at 60fps)

const BULLET_SPEED = 10
const BULLET_LIFETIME = 60 // frames
const SHOOT_COOLDOWN = 10 // frames between shots

const ASTEROID_SPEED_MIN = 1
const ASTEROID_SPEED_MAX = 3
const ASTEROID_SIZES = {
  large: 40,
  medium: 20,
  small: 10,
}

const SCORE_VALUES = {
  large: 20,
  medium: 50,
  small: 100,
}

const INITIAL_LIVES = 3
const ASTEROIDS_PER_LEVEL = 4

/** Game version */
export const ASTEROIDS_VERSION = '1.0.0'

// ============================================================================
// Game Logic (Exported for Testing)
// ============================================================================

/**
 * Create a new ship at the center of the canvas
 */
export function createShip(canvasWidth: number, canvasHeight: number): Ship {
  return {
    position: { x: canvasWidth / 2, y: canvasHeight / 2 },
    velocity: { x: 0, y: 0 },
    angle: -Math.PI / 2, // Point upward
    radius: SHIP_RADIUS,
    invulnerable: true,
    invulnerableTimer: SHIP_INVULNERABLE_TIME,
  }
}

/**
 * Generate random vertices for an asteroid shape
 */
export function generateAsteroidVertices(radius: number): Vector[] {
  const vertices: Vector[] = []
  const numVertices = 8 + Math.floor(Math.random() * 5)

  for (let i = 0; i < numVertices; i++) {
    const angle = (i / numVertices) * Math.PI * 2
    const variance = 0.4 + Math.random() * 0.6
    vertices.push({
      x: Math.cos(angle) * radius * variance,
      y: Math.sin(angle) * radius * variance,
    })
  }

  return vertices
}

/**
 * Create a new asteroid
 */
export function createAsteroid(
  x: number,
  y: number,
  radius: number = ASTEROID_SIZES.large
): Asteroid {
  const angle = Math.random() * Math.PI * 2
  const speed = ASTEROID_SPEED_MIN + Math.random() * (ASTEROID_SPEED_MAX - ASTEROID_SPEED_MIN)

  return {
    position: { x, y },
    velocity: {
      x: Math.cos(angle) * speed,
      y: Math.sin(angle) * speed,
    },
    radius,
    vertices: generateAsteroidVertices(radius),
  }
}

/**
 * Spawn asteroids at the edges of the screen for a new level
 */
export function spawnAsteroids(
  count: number,
  canvasWidth: number,
  canvasHeight: number,
  shipPosition: Vector
): Asteroid[] {
  const asteroids: Asteroid[] = []
  const minDistanceFromShip = 150

  for (let i = 0; i < count; i++) {
    let x: number, y: number

    // Spawn at edges
    if (Math.random() < 0.5) {
      x = Math.random() < 0.5 ? 0 : canvasWidth
      y = Math.random() * canvasHeight
    } else {
      x = Math.random() * canvasWidth
      y = Math.random() < 0.5 ? 0 : canvasHeight
    }

    // Ensure not too close to ship
    const dx = x - shipPosition.x
    const dy = y - shipPosition.y
    if (Math.sqrt(dx * dx + dy * dy) < minDistanceFromShip) {
      x = (x + canvasWidth / 2) % canvasWidth
      y = (y + canvasHeight / 2) % canvasHeight
    }

    asteroids.push(createAsteroid(x, y, ASTEROID_SIZES.large))
  }

  return asteroids
}

/**
 * Create a bullet from the ship
 */
export function createBullet(ship: Ship): Bullet {
  return {
    position: {
      x: ship.position.x + Math.cos(ship.angle) * ship.radius,
      y: ship.position.y + Math.sin(ship.angle) * ship.radius,
    },
    velocity: {
      x: Math.cos(ship.angle) * BULLET_SPEED + ship.velocity.x * 0.5,
      y: Math.sin(ship.angle) * BULLET_SPEED + ship.velocity.y * 0.5,
    },
    lifetime: BULLET_LIFETIME,
  }
}

/**
 * Wrap position around canvas edges
 */
export function wrapPosition(position: Vector, canvasWidth: number, canvasHeight: number): void {
  if (position.x < 0) position.x += canvasWidth
  if (position.x > canvasWidth) position.x -= canvasWidth
  if (position.y < 0) position.y += canvasHeight
  if (position.y > canvasHeight) position.y -= canvasHeight
}

/**
 * Check collision between two circles
 */
export function checkCollision(
  pos1: Vector,
  radius1: number,
  pos2: Vector,
  radius2: number
): boolean {
  const dx = pos1.x - pos2.x
  const dy = pos1.y - pos2.y
  const distance = Math.sqrt(dx * dx + dy * dy)
  return distance < radius1 + radius2
}

/**
 * Get asteroid size category
 */
export function getAsteroidSize(radius: number): 'large' | 'medium' | 'small' {
  if (radius >= ASTEROID_SIZES.large * 0.8) return 'large'
  if (radius >= ASTEROID_SIZES.medium * 0.8) return 'medium'
  return 'small'
}

/**
 * Split an asteroid into smaller pieces
 */
export function splitAsteroid(asteroid: Asteroid): Asteroid[] {
  const size = getAsteroidSize(asteroid.radius)

  if (size === 'small') {
    return [] // Small asteroids are destroyed
  }

  const newRadius = size === 'large' ? ASTEROID_SIZES.medium : ASTEROID_SIZES.small
  const pieces: Asteroid[] = []

  for (let i = 0; i < 2; i++) {
    const newAsteroid = createAsteroid(asteroid.position.x, asteroid.position.y, newRadius)
    // Give them more speed
    newAsteroid.velocity.x *= 1.5
    newAsteroid.velocity.y *= 1.5
    pieces.push(newAsteroid)
  }

  return pieces
}

/**
 * Update ship position and velocity based on input
 */
export function updateShip(
  ship: Ship,
  input: InputState,
  canvasWidth: number,
  canvasHeight: number
): void {
  // Rotation
  if (input.left) ship.angle -= SHIP_ROTATION_SPEED
  if (input.right) ship.angle += SHIP_ROTATION_SPEED

  // Thrust (forward and reverse)
  const thrustDirection = (input.thrust ? 1 : 0) - (input.reverse ? 1 : 0)

  if (thrustDirection !== 0) {
    ship.velocity.x += Math.cos(ship.angle) * SHIP_THRUST * thrustDirection
    ship.velocity.y += Math.sin(ship.angle) * SHIP_THRUST * thrustDirection

    // Limit speed
    const speed = Math.sqrt(ship.velocity.x ** 2 + ship.velocity.y ** 2)
    if (speed > SHIP_MAX_SPEED) {
      ship.velocity.x = (ship.velocity.x / speed) * SHIP_MAX_SPEED
      ship.velocity.y = (ship.velocity.y / speed) * SHIP_MAX_SPEED
    }
  }

  // Apply friction
  ship.velocity.x *= SHIP_FRICTION
  ship.velocity.y *= SHIP_FRICTION

  // Update position
  ship.position.x += ship.velocity.x
  ship.position.y += ship.velocity.y

  // Wrap around screen
  wrapPosition(ship.position, canvasWidth, canvasHeight)

  // Update invulnerability
  if (ship.invulnerable) {
    ship.invulnerableTimer--
    if (ship.invulnerableTimer <= 0) {
      ship.invulnerable = false
    }
  }
}

/**
 * Update all asteroids
 */
export function updateAsteroids(
  asteroids: Asteroid[],
  canvasWidth: number,
  canvasHeight: number
): void {
  for (const asteroid of asteroids) {
    asteroid.position.x += asteroid.velocity.x
    asteroid.position.y += asteroid.velocity.y
    wrapPosition(asteroid.position, canvasWidth, canvasHeight)
  }
}

/**
 * Update all bullets
 */
export function updateBullets(
  bullets: Bullet[],
  canvasWidth: number,
  canvasHeight: number
): Bullet[] {
  return bullets
    .map((bullet) => {
      bullet.position.x += bullet.velocity.x
      bullet.position.y += bullet.velocity.y
      wrapPosition(bullet.position, canvasWidth, canvasHeight)
      bullet.lifetime--
      return bullet
    })
    .filter((bullet) => bullet.lifetime > 0)
}

// ============================================================================
// Rendering
// ============================================================================

function drawShip(ctx: CanvasRenderingContext2D, ship: Ship): void {
  // Blink when invulnerable
  if (ship.invulnerable && Math.floor(ship.invulnerableTimer / 5) % 2 === 0) {
    return
  }

  ctx.save()
  ctx.translate(ship.position.x, ship.position.y)
  ctx.rotate(ship.angle)

  ctx.strokeStyle = '#06b6d4' // arcade-cyan
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(ship.radius, 0)
  ctx.lineTo(-ship.radius * 0.7, -ship.radius * 0.6)
  ctx.lineTo(-ship.radius * 0.4, 0)
  ctx.lineTo(-ship.radius * 0.7, ship.radius * 0.6)
  ctx.closePath()
  ctx.stroke()

  ctx.restore()
}

function drawThrustFlame(ctx: CanvasRenderingContext2D, ship: Ship): void {
  ctx.save()
  ctx.translate(ship.position.x, ship.position.y)
  ctx.rotate(ship.angle)

  ctx.strokeStyle = '#f97316' // orange
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(-ship.radius * 0.5, -ship.radius * 0.3)
  ctx.lineTo(-ship.radius * 1.2, 0)
  ctx.lineTo(-ship.radius * 0.5, ship.radius * 0.3)
  ctx.stroke()

  ctx.restore()
}

function drawAsteroid(ctx: CanvasRenderingContext2D, asteroid: Asteroid): void {
  ctx.save()
  ctx.translate(asteroid.position.x, asteroid.position.y)

  ctx.strokeStyle = '#9ca3af' // gray
  ctx.lineWidth = 2
  ctx.beginPath()

  const vertices = asteroid.vertices
  ctx.moveTo(vertices[0].x, vertices[0].y)

  for (let i = 1; i < vertices.length; i++) {
    ctx.lineTo(vertices[i].x, vertices[i].y)
  }

  ctx.closePath()
  ctx.stroke()

  ctx.restore()
}

function drawBullet(ctx: CanvasRenderingContext2D, bullet: Bullet): void {
  ctx.fillStyle = '#22c55e' // arcade-green
  ctx.beginPath()
  ctx.arc(bullet.position.x, bullet.position.y, 3, 0, Math.PI * 2)
  ctx.fill()
}

function drawUI(
  ctx: CanvasRenderingContext2D,
  score: number,
  lives: number,
  canvasWidth: number
): void {
  ctx.fillStyle = '#ffffff'
  ctx.font = '16px "Press Start 2P", monospace'
  ctx.textAlign = 'left'
  ctx.fillText(`SCORE: ${score}`, 20, 30)

  ctx.textAlign = 'right'
  ctx.fillText(`LIVES: ${lives}`, canvasWidth - 20, 30)
}

function drawStartScreen(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  highScores: HighScoreEntry[]
): void {
  ctx.fillStyle = 'rgba(0, 0, 0, 0.8)'
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  ctx.fillStyle = '#06b6d4' // arcade-cyan
  ctx.font = '48px "Press Start 2P", monospace'
  ctx.textAlign = 'center'
  ctx.fillText('ASTEROIDS', canvasWidth / 2, canvasHeight / 2 - 120)

  ctx.fillStyle = '#ffffff'
  ctx.font = '14px "Press Start 2P", monospace'
  ctx.fillText('Destroy asteroids and survive', canvasWidth / 2, canvasHeight / 2 - 70)

  // Show high scores if available
  if (highScores.length > 0) {
    ctx.fillStyle = '#f97316' // orange
    ctx.font = '10px "Press Start 2P", monospace'
    ctx.fillText('HIGH SCORES', canvasWidth / 2, canvasHeight / 2 - 35)

    ctx.fillStyle = '#9ca3af' // gray
    ctx.font = '9px "Press Start 2P", monospace'
    const topScores = highScores.slice(0, 3)
    topScores.forEach((entry, index) => {
      const y = canvasHeight / 2 - 10 + index * 18
      ctx.fillText(
        `${index + 1}. ${entry.name}  ${entry.score.toLocaleString()}`,
        canvasWidth / 2,
        y
      )
    })
  }

  const controlsY = highScores.length > 0 ? canvasHeight / 2 + 55 : canvasHeight / 2 + 20

  ctx.fillStyle = '#22c55e' // arcade-green
  ctx.font = '12px "Press Start 2P", monospace'
  ctx.fillText('CONTROLS', canvasWidth / 2, controlsY)

  ctx.fillStyle = '#9ca3af' // gray
  ctx.font = '10px "Press Start 2P", monospace'
  ctx.fillText('Arrow Keys / WASD - Move', canvasWidth / 2, controlsY + 25)
  ctx.fillText('Space - Shoot', canvasWidth / 2, controlsY + 45)

  ctx.fillStyle = '#06b6d4' // arcade-cyan
  ctx.font = '14px "Press Start 2P", monospace'
  ctx.fillText('Press SPACE to start', canvasWidth / 2, controlsY + 90)
}

function drawGameOver(
  ctx: CanvasRenderingContext2D,
  score: number,
  canvasWidth: number,
  canvasHeight: number
): void {
  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  ctx.fillStyle = '#ec4899' // arcade-pink
  ctx.font = '32px "Press Start 2P", monospace'
  ctx.textAlign = 'center'
  ctx.fillText('GAME OVER', canvasWidth / 2, canvasHeight / 2 - 40)

  ctx.fillStyle = '#ffffff'
  ctx.font = '16px "Press Start 2P", monospace'
  ctx.fillText(`FINAL SCORE: ${score}`, canvasWidth / 2, canvasHeight / 2 + 20)

  ctx.fillStyle = '#06b6d4' // arcade-cyan
  ctx.font = '12px "Press Start 2P", monospace'
  ctx.fillText('Press ENTER to restart', canvasWidth / 2, canvasHeight / 2 + 60)
}

// ============================================================================
// Main Game Loop
// ============================================================================

/**
 * Initialize and run the Asteroids game
 * @param container The HTML element to render the game in
 * @param onGameOver Optional callback called when game ends with the final score
 * @returns Cleanup function to stop the game
 */
export function loadAsteroids(container: HTMLElement, onGameOver?: GameOverCallback): () => void {
  // Create canvas
  const canvas = document.createElement('canvas')
  canvas.style.width = '100%'
  canvas.style.height = '100%'
  canvas.style.display = 'block'
  container.innerHTML = ''
  container.appendChild(canvas)

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    console.error('Could not get canvas context')
    return () => {}
  }

  // Store reference to avoid null checks in nested functions
  const context: CanvasRenderingContext2D = ctx

  // Resize canvas to match container
  function resizeCanvas(): void {
    canvas.width = container.clientWidth
    canvas.height = container.clientHeight
  }
  resizeCanvas()

  const resizeObserver = new ResizeObserver(resizeCanvas)
  resizeObserver.observe(container)

  // Track if game over callback has been called
  let gameOverCallbackFired = false

  // Game state
  let state: GameState = {
    ship: createShip(canvas.width, canvas.height),
    asteroids: [],
    bullets: [],
    score: 0,
    lives: INITIAL_LIVES,
    gameOver: false,
    paused: false,
    level: 1,
    started: false,
  }

  // Asteroids will spawn when game starts

  // Input state
  const input: InputState = {
    left: false,
    right: false,
    thrust: false,
    reverse: false,
    shoot: false,
  }

  let shootCooldown = 0
  let running = true

  // Input handlers
  function handleKeyDown(e: KeyboardEvent): void {
    if (state.gameOver) {
      if (e.key === 'Enter') {
        // Restart game
        state = {
          ship: createShip(canvas.width, canvas.height),
          asteroids: spawnAsteroids(ASTEROIDS_PER_LEVEL, canvas.width, canvas.height, {
            x: canvas.width / 2,
            y: canvas.height / 2,
          }),
          bullets: [],
          score: 0,
          lives: INITIAL_LIVES,
          gameOver: false,
          paused: false,
          level: 1,
          started: false,
        }
      }
      return
    }

    if (!state.started) {
      if (e.key === ' ') {
        state.started = true
        // Spawn initial asteroids when game starts
        state.asteroids = spawnAsteroids(
          ASTEROIDS_PER_LEVEL,
          canvas.width,
          canvas.height,
          state.ship.position
        )
        e.preventDefault()
      }
      return
    }

    switch (e.key) {
      case 'ArrowLeft':
      case 'a':
        input.left = true
        e.preventDefault()
        break
      case 'ArrowRight':
      case 'd':
        input.right = true
        e.preventDefault()
        break
      case 'ArrowUp':
      case 'w':
        input.thrust = true
        e.preventDefault()
        break
      case 'ArrowDown':
      case 's':
        input.reverse = true
        e.preventDefault()
        break
      case ' ':
        input.shoot = true
        e.preventDefault()
        break
    }
  }

  function handleKeyUp(e: KeyboardEvent): void {
    switch (e.key) {
      case 'ArrowLeft':
      case 'a':
        input.left = false
        break
      case 'ArrowRight':
      case 'd':
        input.right = false
        break
      case 'ArrowUp':
      case 'w':
        input.thrust = false
        break
      case 'ArrowDown':
      case 's':
        input.reverse = false
        break
      case ' ':
        input.shoot = false
        break
    }
  }

  document.addEventListener('keydown', handleKeyDown)
  document.addEventListener('keyup', handleKeyUp)

  // Game loop
  function gameLoop(): void {
    if (!running) return

    const width = canvas.width
    const height = canvas.height

    // Clear canvas
    context.fillStyle = '#0a0a0a'
    context.fillRect(0, 0, width, height)

    // Show start screen if game hasn't started
    if (!state.started) {
      const highScores = getHighScores('asteroids')
      drawStartScreen(context, width, height, highScores)
      requestAnimationFrame(gameLoop)
      return
    }

    if (!state.gameOver && !state.paused) {
      // Update ship
      updateShip(state.ship, input, width, height)

      // Shooting
      if (input.shoot && shootCooldown <= 0) {
        state.bullets.push(createBullet(state.ship))
        shootCooldown = SHOOT_COOLDOWN
      }
      shootCooldown--

      // Update asteroids
      updateAsteroids(state.asteroids, width, height)

      // Update bullets
      state.bullets = updateBullets(state.bullets, width, height)

      // Check bullet-asteroid collisions
      const newAsteroids: Asteroid[] = []
      const bulletsToRemove = new Set<number>()

      for (let i = state.asteroids.length - 1; i >= 0; i--) {
        const asteroid = state.asteroids[i]
        let hit = false

        for (let j = 0; j < state.bullets.length; j++) {
          if (bulletsToRemove.has(j)) continue

          const bullet = state.bullets[j]
          if (checkCollision(bullet.position, 3, asteroid.position, asteroid.radius)) {
            hit = true
            bulletsToRemove.add(j)

            // Add score
            const size = getAsteroidSize(asteroid.radius)
            state.score += SCORE_VALUES[size]

            // Split asteroid
            newAsteroids.push(...splitAsteroid(asteroid))
            break
          }
        }

        if (!hit) {
          newAsteroids.push(asteroid)
        }
      }

      state.asteroids = newAsteroids
      state.bullets = state.bullets.filter((_, i) => !bulletsToRemove.has(i))

      // Check ship-asteroid collisions
      if (!state.ship.invulnerable) {
        for (const asteroid of state.asteroids) {
          if (
            checkCollision(
              state.ship.position,
              state.ship.radius * 0.6,
              asteroid.position,
              asteroid.radius
            )
          ) {
            state.lives--

            if (state.lives <= 0) {
              state.gameOver = true
              // Fire game over callback once
              if (!gameOverCallbackFired && onGameOver) {
                gameOverCallbackFired = true
                onGameOver(state.score)
              }
            } else {
              // Respawn ship
              state.ship = createShip(width, height)
            }
            break
          }
        }
      }

      // Check for level completion
      if (state.asteroids.length === 0) {
        state.level++
        state.asteroids = spawnAsteroids(
          ASTEROIDS_PER_LEVEL + state.level - 1,
          width,
          height,
          state.ship.position
        )
        state.ship.invulnerable = true
        state.ship.invulnerableTimer = SHIP_INVULNERABLE_TIME
      }
    }

    // Draw everything
    for (const asteroid of state.asteroids) {
      drawAsteroid(context, asteroid)
    }

    for (const bullet of state.bullets) {
      drawBullet(context, bullet)
    }

    if (!state.gameOver) {
      drawShip(context, state.ship)
      if (input.thrust && !state.ship.invulnerable) {
        drawThrustFlame(context, state.ship)
      }
    }

    drawUI(context, state.score, state.lives, width)

    if (state.gameOver) {
      drawGameOver(context, state.score, width, height)
    }

    requestAnimationFrame(gameLoop)
  }

  // Start the game loop
  gameLoop()

  // Return cleanup function
  return () => {
    running = false
    document.removeEventListener('keydown', handleKeyDown)
    document.removeEventListener('keyup', handleKeyUp)
    resizeObserver.disconnect()
    container.innerHTML = ''
  }
}
